/**
 * @fileoverview Toolbar strip above the `DashboardCanvasBuilder` 12-column grid.
 * @module ui-admin/components/DashboardCanvasBuilder/DashboardCanvasToolbar
 *
 * Responsibilities: - Render surface filter tabs (`All Widgets`, `KPIs & Charts`, `Tables & Filters`),
 *                     `Grid 12-col` indicator, optional `Help` and `Tour` triggers, and the
 *                     `+ Add widget` button (`data-tour="dash-tour-add-widget"`).
 * Dependencies:     - React, lucide-react
 *                   - ./dashboardTourSteps (DASHBOARD_TOUR_TARGET)
 * Architecture:     - Presentation-only toolbar; mirrors `ReportCanvasToolbar` and `DocumentCanvasBuilder` header.
 * Notes:            - Hidden in `readOnly` mode.
 */

import React from 'react';
import { Plus, HelpCircle, Compass, Grid3X3 } from 'lucide-react';
import { DASHBOARD_TOUR_TARGET } from './dashboardTourSteps';

export type DashboardSurfaceFilter = 'all' | 'kpis-charts' | 'tables-filters';

/**
 * @contract DashboardCanvasToolbarProps
 * Purpose: Props for `DashboardCanvasToolbar`.
 * Fields:  surfaceFilter, onSurfaceFilterChange, columns, rowHeight, widgetCount, onAddWidgetClick,
 *          onOpenHelp?, onStartTour?, readOnly?
 * Rules:   Presentation-only.
 * Sync:    N/A.
 */
export interface DashboardCanvasToolbarProps {
  surfaceFilter: DashboardSurfaceFilter;
  onSurfaceFilterChange: (next: DashboardSurfaceFilter) => void;
  columns: number;
  rowHeight: number;
  widgetCount: number;
  onAddWidgetClick: () => void;
  onOpenHelp?: () => void;
  onStartTour?: () => void;
  readOnly?: boolean;
}

const SURFACE_TABS: Array<{ id: DashboardSurfaceFilter; label: string }> = [
  { id: 'all', label: 'All Widgets' },
  { id: 'kpis-charts', label: 'KPIs & Charts' },
  { id: 'tables-filters', label: 'Tables & Filters' },
];

/**
 * @component DashboardCanvasToolbar
 * Purpose:     Render the canvas toolbar above the 12-column dashboard grid.
 * Props:       DashboardCanvasToolbarProps.
 * Behavior:    Switches surface filter tabs and triggers the widget palette drawer.
 * Data Source: none (presentation-only).
 * Constraints: no persistence.
 * Performance: stateless.
 */
export function DashboardCanvasToolbar({
  surfaceFilter,
  onSurfaceFilterChange,
  columns,
  rowHeight,
  widgetCount,
  onAddWidgetClick,
  onOpenHelp,
  onStartTour,
  readOnly = false,
}: DashboardCanvasToolbarProps) {
  if (readOnly) return null;

  return (
    <div
      data-testid="dashboard-canvas-toolbar"
      className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white border-b border-gray-200 shrink-0"
    >
      <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg" role="tablist">
        {SURFACE_TABS.map((tab) => {
          const active = surfaceFilter === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSurfaceFilterChange(tab.id)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                active
                  ? 'bg-white text-gray-900 shadow-2xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 px-2 py-1 bg-gray-50 rounded border border-gray-200">
          <Grid3X3 className="h-3.5 w-3.5 text-gray-400" />
          {`${columns}-col · ${rowHeight}px · ${widgetCount} widget${widgetCount !== 1 ? 's' : ''}`}
        </span>

        {onOpenHelp && (
          <button
            type="button"
            onClick={onOpenHelp}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            Help
          </button>
        )}

        {onStartTour && (
          <button
            type="button"
            onClick={onStartTour}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
          >
            <Compass className="h-3.5 w-3.5" />
            Tour
          </button>
        )}

        <button
          type="button"
          data-tour={DASHBOARD_TOUR_TARGET.addWidget}
          data-testid="open-widget-palette-btn"
          onClick={onAddWidgetClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-md shadow-2xs transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add widget
        </button>
      </div>
    </div>
  );
}

export default DashboardCanvasToolbar;
